import React, { useState, useEffect, useContext } from "react"
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Row,
  UncontrolledAlert,
  Card,
  CardBody,
  Modal,
} from "reactstrap"
import TenantConnectionContext from "./TenantConnectionContext"
import LoadingOverlay from "react-loading-overlay"
import Error from "pages/NotificationModal/Error"
import Success from "./Success"
import Confirmation from "./Confirmation"
import SecretInput from "./SecretInput"

// ALVYS (Alvys) is currently inbound-only: the Qued poller (tms_alvys_ib_api)
// authenticates to Alvys with an OAuth2 client-credentials flow whose fields live
// as flat keys in config (auth_url/client_id/client_secret/audience/api_version),
// with the Alvys API base in the url column and polling gated by poll_alvys_loads.
// This form edits those; method (oauth2) and the unused username/password/token
// columns are preserved server-side.
const isMasked = v => typeof v === "string" && /^(\*{1,2}|.\*\*.)$/.test(v)
const hasVal = v => v != null && String(v).trim() !== ""

const AlvysAuthForm = ({ hideForm }) => {
  const {
    loading,
    success,
    error,
    errorMessage,
    setSuccess,
    setError,
    tenantData,
    submitUpdate,
    revealSecret,
    updateResponse,
  } = useContext(TenantConnectionContext)

  const [url, setUrl] = useState("")
  const [authUrl, setAuthUrl] = useState("")
  const [clientId, setClientId] = useState("")
  const [clientSecret, setClientSecret] = useState("")
  const [audience, setAudience] = useState("")
  const [apiVersion, setApiVersion] = useState("")
  const [pollAlvysLoads, setPollAlvysLoads] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (!tenantData) return
    setUrl(tenantData.url || "")
    const al = tenantData.alvys || {}
    setAuthUrl(al.auth_url || "")
    setClientId(al.client_id || "")
    setClientSecret(al.client_secret || "")
    setAudience(al.audience || "")
    setApiVersion(al.api_version || "")
    setPollAlvysLoads(!!al.poll_alvys_loads)
  }, [tenantData])

  const fieldRow = (label, node) => (
    <Row className="mb-2" key={label}>
      <Label className="col-sm-3 col-form-label">{label}</Label>
      <Col sm={6} className="ms-4">
        {node}
      </Col>
    </Row>
  )

  const textRow = (label, value, setter, placeholder) =>
    fieldRow(
      label,
      <Input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={e => setter(e.target.value)}
      />
    )

  const handleSubmit = () => {
    setErrorMsg("")
    const missing = []
    if (!hasVal(url)) missing.push("API Base URL")
    if (!hasVal(clientId)) missing.push("Client ID")
    if (!(hasVal(clientSecret) || isMasked(clientSecret))) missing.push("Client Secret")
    if (missing.length) {
      setErrorMsg(`Required: ${missing.join(", ")}`)
      return
    }
    setShowConfirmation(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false)
    await submitUpdate({
      url,
      alvys: {
        auth_url: authUrl,
        client_id: clientId,
        client_secret: clientSecret,
        audience,
        api_version: apiVersion,
        poll_alvys_loads: pollAlvysLoads,
      },
    })
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    hideForm()
  }
  const closeSuccess = () => {
    setSuccess(false)
    hideForm()
  }
  const togError = () => setError(false)

  if (!tenantData) {
    return (
      <LoadingOverlay active={loading} spinner>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h5>Loading...</h5>
        </div>
      </LoadingOverlay>
    )
  }

  return (
    <>
      <Modal isOpen={success} toggle={closeSuccess} centered size="md">
        <Success data={updateResponse} cancel={closeSuccess} />
      </Modal>
      <Modal isOpen={error} toggle={togError} centered size="sm">
        <Error
          message={errorMessage || "Something went wrong"}
          closeModal={togError}
        />
      </Modal>
      <Modal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        centered
        size="sm"
      >
        <Confirmation
          message="Do you want to save these changes?"
          confirm={handleConfirmSubmit}
          cancel={() => setShowConfirmation(false)}
        />
      </Modal>

      <LoadingOverlay active={loading} spinner>
        <Card className="p-3">
          <div className="mb-3">
            <h6 className="text-primary">Edit Alvys Integration Auth</h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                {errorMsg && (
                  <div className="mb-3">
                    <UncontrolledAlert color="danger" role="alert">
                      {errorMsg}
                    </UncontrolledAlert>
                  </div>
                )}
                <Row className="mb-2">
                  <Col sm={9} className="ms-4">
                    <small className="text-muted">
                      Alvys is an inbound integration — Qued polls Alvys for loads
                      using OAuth2 client credentials.
                    </small>
                  </Col>
                </Row>
                <Form>
                  {textRow("API Base URL", url, setUrl, "https://integrations.alvys.com")}
                  {textRow(
                    "Auth URL",
                    authUrl,
                    setAuthUrl,
                    "https://auth.alvys.com"
                  )}
                  {textRow("Client ID", clientId, setClientId, "Enter client id")}
                  {fieldRow(
                    "Client Secret",
                    <SecretInput
                      value={clientSecret}
                      onChange={setClientSecret}
                      label="Client Secret"
                      placeholder="Enter client secret"
                      revealPath="client_secret"
                      onReveal={revealSecret}
                    />
                  )}
                  {textRow(
                    "Audience",
                    audience,
                    setAudience,
                    "https://api.alvys.com/public/"
                  )}
                  {textRow("API Version", apiVersion, setApiVersion, "v2.0")}
                  {fieldRow(
                    "Poll Alvys Loads",
                    <div className="form-check mt-2">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="alvys-poll-loads"
                        checked={pollAlvysLoads}
                        onChange={e => setPollAlvysLoads(e.target.checked)}
                      />
                      <Label
                        className="form-check-label"
                        htmlFor="alvys-poll-loads"
                      >
                        Enable polling Alvys for loads
                      </Label>
                    </div>
                  )}
                </Form>
              </CardBody>
            </Card>

            <div className="d-flex justify-content-end mt-3 me-4">
              <div className="d-flex gap-2">
                <Button onClick={handleSubmit} className="btn" color="primary">
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  className="btn"
                  color="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>
    </>
  )
}

export default AlvysAuthForm
